'use client';

import * as React from 'react';
import Image from 'next/image';
import NiceModal, { type NiceModalHocProps } from '@ebay/nice-modal-react';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  RotateCcw,
  XIcon,
  ZoomIn,
  ZoomOut
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@workspace/ui/components/dialog';
import { cn } from '@workspace/ui/lib/utils';

import { useEnhancedModal } from '~/hooks/use-enhanced-modal';

type ImageData = {
  url: string;
  title?: string;
  filename?: string;
};

export type FullscreenImageModalProps = NiceModalHocProps & {
  images: ImageData[];
  currentIndex?: number;
  onIndexChange?: (index: number) => void;
};

type ActionButtonProps = {
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  disabled?: boolean;
  'aria-label': string;
  title?: string;
  className?: string;
};

function ActionButton({
  onClick,
  children,
  disabled,
  'aria-label': ariaLabel,
  title,
  className
}: ActionButtonProps): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick(e);
      }}
      disabled={disabled}
      className={cn(
        'bg-black/50 hover:bg-white/70 text-white hover:text-black p-2 rounded-full transition-colors duration-200',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      aria-label={ariaLabel}
      title={title}
    >
      {children}
    </button>
  );
}

async function downloadImage(
  imageUrl: string,
  filename?: string
): Promise<void> {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download =
      filename || `image-${new Date().toISOString().split('T')[0]}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download image:', error);
    window.open(imageUrl, '_blank');
  }
}

export const FullscreenImageModal = NiceModal.create<FullscreenImageModalProps>(
  ({ images, currentIndex = 0, onIndexChange }) => {
    const modal = useEnhancedModal();
    const [internalCurrentIndex, setInternalCurrentIndex] =
      React.useState(currentIndex);

    // Zoom state
    const [scale, setScale] = React.useState(1);
    const [position, setPosition] = React.useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = React.useState(false);
    const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });
    const [lastTouchDistance, setLastTouchDistance] = React.useState(0);

    // Update internal state when props change
    React.useEffect(() => {
      setInternalCurrentIndex(currentIndex);
    }, [currentIndex]);

    // Reset zoom when image changes
    React.useEffect(() => {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }, [internalCurrentIndex]);

    const currentImage = images[internalCurrentIndex];
    const isMultiImage = images.length > 1;

    const goToPreviousImage = () => {
      if (isMultiImage && internalCurrentIndex > 0) {
        const newIndex = internalCurrentIndex - 1;
        setInternalCurrentIndex(newIndex);
        onIndexChange?.(newIndex);
      }
    };

    const goToNextImage = () => {
      if (isMultiImage && internalCurrentIndex < images.length - 1) {
        const newIndex = internalCurrentIndex + 1;
        setInternalCurrentIndex(newIndex);
        onIndexChange?.(newIndex);
      }
    };

    const handleDownload = () => {
      const filename =
        currentImage.filename ||
        `${currentImage.title || 'image'}-${new Date().toISOString().split('T')[0]}.jpg`;
      downloadImage(currentImage.url, filename);
    };

    // Zoom functions
    const zoomIn = () => {
      setScale((prev) => Math.min(prev * 1.2, 5));
    };

    const zoomOut = () => {
      setScale((prev) => Math.max(prev / 1.2, 0.5));
    };

    const resetZoom = () => {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    };

    // Mouse wheel zoom
    const handleWheel = (e: React.WheelEvent) => {
      e.preventDefault();
      if (e.deltaY < 0) {
        zoomIn();
      } else {
        zoomOut();
      }
    };

    // Touch handlers for pinch-to-zoom
    const getTouchDistance = (touches: React.TouchList) => {
      if (touches.length < 2) return 0;
      const touch1 = touches[0];
      const touch2 = touches[1];
      return Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
          Math.pow(touch2.clientY - touch1.clientY, 2)
      );
    };

    const handleTouchStart = (e: React.TouchEvent) => {
      if (e.touches.length === 2) {
        setLastTouchDistance(getTouchDistance(e.touches as React.TouchList));
      } else if (e.touches.length === 1 && scale > 1) {
        setIsDragging(true);
        setDragStart({
          x: e.touches[0].clientX - position.x,
          y: e.touches[0].clientY - position.y
        });
      }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
      e.preventDefault();

      if (e.touches.length === 2) {
        const distance = getTouchDistance(e.touches as React.TouchList);
        if (lastTouchDistance > 0) {
          const scaleChange = distance / lastTouchDistance;
          setScale((prev) => Math.min(Math.max(prev * scaleChange, 0.5), 5));
        }
        setLastTouchDistance(distance);
      } else if (e.touches.length === 1 && isDragging && scale > 1) {
        const touch = e.touches[0];
        setPosition({
          x: touch.clientX - dragStart.x,
          y: touch.clientY - dragStart.y
        });
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      setLastTouchDistance(0);
    };

    // Mouse drag handlers
    const handleMouseDown = (e: React.MouseEvent) => {
      if (scale > 1) {
        setIsDragging(true);
        setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
      }
    };

    const handleMouseMove = (e: React.MouseEvent) => {
      if (isDragging && scale > 1) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    // Handle keyboard navigation
    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (!modal.visible) return;

        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            goToPreviousImage();
            break;
          case 'ArrowRight':
            event.preventDefault();
            goToNextImage();
            break;
          case 'Escape':
            event.preventDefault();
            modal.handleClose();
            break;
          case '+':
          case '=':
            event.preventDefault();
            zoomIn();
            break;
          case '-':
            event.preventDefault();
            zoomOut();
            break;
          case '0':
            event.preventDefault();
            resetZoom();
            break;
        }
      };

      if (modal.visible) {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
      }
    }, [modal.visible, internalCurrentIndex, isMultiImage]);

    if (!currentImage) return <></>;

    return (
      <Dialog
        open={modal.visible}
        onOpenChange={modal.handleOpenChange}
      >
        <DialogContent
          className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-none"
          onClose={modal.handleClose}
          onAnimationEndCapture={modal.handleAnimationEndCapture}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{currentImage.title || 'Image'}</DialogTitle>
          </DialogHeader>

          <div
            className="relative flex items-center justify-center min-h-[80vh] overflow-hidden"
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{
              cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
            }}
          >
            {/* Navigation buttons for multiple images */}
            {isMultiImage && (
              <>
                <ActionButton
                  onClick={goToPreviousImage}
                  disabled={internalCurrentIndex === 0}
                  aria-label="Previous image"
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-10"
                >
                  <ChevronLeft className="h-6 w-6" />
                </ActionButton>

                <ActionButton
                  onClick={goToNextImage}
                  disabled={internalCurrentIndex === images.length - 1}
                  aria-label="Next image"
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-10"
                >
                  <ChevronRight className="h-6 w-6" />
                </ActionButton>
              </>
            )}

            {/* Image counter for multiple images */}
            {isMultiImage && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                {internalCurrentIndex + 1} of {images.length}
              </div>
            )}

            {/* Action buttons */}
            <div className="absolute top-4 right-4 z-10 flex gap-2">
              <ActionButton
                onClick={handleDownload}
                aria-label="Download image"
                title="Download image"
              >
                <Download className="h-5 w-5" />
              </ActionButton>
              <ActionButton
                onClick={modal.handleClose}
                aria-label="Close"
                title="Close"
              >
                <XIcon className="h-5 w-5" />
              </ActionButton>
            </div>

            {/* Zoom controls */}
            <div className="absolute top-4 left-4 z-10 flex gap-2">
              <ActionButton
                onClick={zoomOut}
                aria-label="Zoom out"
                title="Zoom out"
              >
                <ZoomOut className="h-5 w-5" />
              </ActionButton>
              <ActionButton
                onClick={resetZoom}
                aria-label="Reset zoom"
                title="Reset zoom"
              >
                <RotateCcw className="h-5 w-5" />
              </ActionButton>
              <ActionButton
                onClick={zoomIn}
                aria-label="Zoom in"
                title="Zoom in"
              >
                <ZoomIn className="h-5 w-5" />
              </ActionButton>
            </div>

            {/* Main image */}
            <div
              className="relative max-w-full max-h-full"
              style={{
                transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                transformOrigin: 'center center',
                transition: isDragging ? 'none' : 'transform 0.2s ease-out'
              }}
            >
              <Image
                src={currentImage.url}
                alt={currentImage.title || 'Image'}
                width={500}
                height={500}
                className="w-auto h-full max-w-full max-h-[80vh] object-contain"
                priority
                draggable={false}
              />
            </div>

            {/* Keyboard navigation hint */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-xs opacity-70">
              {isMultiImage && 'Use ← → arrow keys to navigate • '}
              Use +/- to zoom • 0 to reset • ESC to close
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
);
